import {
  Box,
  Container,
  containerClasses,
  Step,
  stepConnectorClasses,
  stepIconClasses,
  StepLabel,
  stepLabelClasses,
  Stepper,
  Typography,
  typographyClasses,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { IdSubmissionForm } from 'public/components/id-submission/forms/id-submission-form';
import { config } from 'public/styles/config';
import { useIdSubmission } from './hooks/use-id-submission';

export const IdSubmission = () => {
  const { activeStep, emailVerified, handleNextStep, setEmailVerified, steps } = useIdSubmission();
  const { t } = useTranslation('web-common');

  return (
    <StyledContainer>
      {emailVerified ? (
        <StepperContainer>
          <StyledStepper activeStep={activeStep} alternativeLabel>
            {steps.map((step) => (
              <Step key={step.id}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </StyledStepper>
        </StepperContainer>
      ) : null}
      <StyledHeader>
        <StyledTitle>{t('id-submission.screen-title')}</StyledTitle>
      </StyledHeader>
      <IdSubmissionForm
        step={activeStep}
        onNextStep={handleNextStep}
        emailVerified={emailVerified}
        setEmailVerified={setEmailVerified}
      />
    </StyledContainer>
  );
};

const StepperContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  marginTop: theme.spacing(12),
  position: 'absolute',
  top: '0',
  left: '0',
  paddingInline: theme.spacing(2),
  boxSizing: 'border-box',

  [theme.breakpoints.down('md')]: {
    position: 'relative',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  [`&.${typographyClasses.root}`]: {
    fontSize: theme.typography.pxToRem(36),
    fontWeight: 700,
    color: theme.palette.lwBlack[100],
    fontVariationSettings: '"wght" 700',
    lineHeight: '40px',

    [theme.breakpoints.down('md')]: {
      fontSize: theme.typography.pxToRem(24),
    },
  },
}));

const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(12),

  [theme.breakpoints.down('md')]: {
    marginBottom: 0,
    marginTop: 0,
  },
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  [`&.${containerClasses.root}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    paddingTop: theme.spacing(12),
    '& *': {
      fontFamily: config.fontFamily,
    },

    [theme.breakpoints.down('md')]: {
      justifyContent: 'flex-start',
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(16),
    },
  },
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    [`& .${stepLabelClasses.label}`]: {
      fontSize: theme.typography.pxToRem(12),
      lineHeight: 1.2,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: config.colors.background,
    borderWidth: '3px',
  },
  [`& .${stepConnectorClasses.active} .${stepConnectorClasses.line}, & .${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]:
    {
      borderColor: config.colors.primary,
    },
  [`& .${stepIconClasses.root}`]: {
    color: config.colors.background,
  },
  [`& .${stepIconClasses.root}.${stepIconClasses.active}, & .${stepIconClasses.root}.${stepIconClasses.completed}`]:
    {
      color: config.colors.primaryLight,
    },
  [`& .${stepIconClasses.text}`]: {
    fill: theme.palette.lwBlack[20],
  },
  [`& .${stepIconClasses.active} .${stepIconClasses.text}, & .${stepIconClasses.completed} .${stepIconClasses.text}`]:
    {
      fill: config.colors.primary,
    },
}));
